"use client";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/tabs";
import { cn } from "@/lib/utils";
import { ChartLine, Table } from "lucide-react";
import { createContext, useState } from "react";

export type ChartTimeSplit = "hourly" | "daily";

export const hourlyAxisFormatter = (value: string) => {
  const date = new Date(value);

  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

export const hourlyLabelFormatter = (value: string) => {
  const date = new Date(value);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
};

export const dailyLabelFormatter = (value: string) => {
  const date = new Date(value);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

export const CHART_TYPES = ["lineChart", "table"] as const;
export type ChartType = (typeof CHART_TYPES)[number];

export const TimeSplitContext = createContext<ChartTimeSplit>("hourly");
function ChartTemplate({
  chart,
  table,
  title = "",
  description = "",
  summaryMetric,
  summaryMetricLabel = "",
  timeSplittable,
  defaultTimeSplit = "hourly",
}: {
  chart: React.ReactNode;
  table: React.ReactNode;
  title?: React.ReactNode;
  description?: string;
  summaryMetric?: string;
  summaryMetricLabel?: string;
  timeSplittable: boolean;
  defaultTimeSplit?: ChartTimeSplit;
}) {
  const [timeSplit, setTimeSplit] = useState<ChartTimeSplit>(defaultTimeSplit);
  const [chartType, setChartType] = useState<ChartType>("lineChart");

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="flex flex-col items-stretch space-y-0 border-b p-0 sm:flex-row ">
        <div className="flex flex-1 flex-col justify-center gap-1 px-6 py-5 sm:py-6">
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
        {table && (
          <div className="flex flex-col">
            {CHART_TYPES.map(key => {
              return (
                <button
                  key={key}
                  className={cn(
                    "relative z-30 flex flex-1 flex-col justify-start gap-1 border-t px-6 py-4 text-left even:border-l data-[active=true]:bg-muted/50 sm:border-l sm:border-t-0 sm:px-8 sm:py-6 ",
                    chartType === key && "bg-slate-200",
                  )}
                  onClick={() => setChartType(key)}
                >
                  <div className="flex justify-start items-center gap-2">
                    {key === "lineChart" && <ChartLine />}
                    {key === "table" && <Table />}
                    <span className="text-sm font-bold leading-none ml-2">
                      {key === "lineChart" ? "Line Chart" : "Table"}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {summaryMetric && (
          <div className="flex">
            <div className="flex flex-1 flex-col justify-center gap-1 border-t px-6 py-4 text-left even:border-l data-[active=true]:bg-muted/50 sm:border-l sm:border-t-0 sm:px-8 sm:py-6">
              <span className="text-xs text-muted-foreground">{summaryMetricLabel}</span>
              <span className="text-lg font-bold leading-none sm:text-3xl">{summaryMetric}</span>
            </div>
          </div>
        )}
      </CardHeader>
      {timeSplittable && (
        <div className="flex justify-end data-[active=true]:bg-muted/50 sm:px-2 sm:py-4">
          <Tabs value={timeSplit} onValueChange={value => setTimeSplit(value as ChartTimeSplit)} className="space-y-4">
            <TabsList>
              <TabsTrigger value="hourly" onClick={e => setTimeSplit("hourly")}>
                Hourly
              </TabsTrigger>
              <TabsTrigger value="daily" onClick={e => setTimeSplit("daily")}>
                Daily
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      )}
      <CardContent
        className={cn(
          "flex-1 max-h-full",
          `${chartType === "table" && "overflow-y-scroll"}`,
          timeSplittable ? "" : "mt-2",
        )}
      >
        <TimeSplitContext.Provider value={timeSplit}>
          <>
            {chartType === "lineChart" && chart}
            {chartType === "table" && table}
          </>
        </TimeSplitContext.Provider>
      </CardContent>
    </Card>
  );
}

export default ChartTemplate;
